import { ChatMessage, MessagePart } from './aiAssistantModel'
import { LookupQuery, LookupResult } from './aiAssistantApi'
import { RatingState } from './messageRatingModel'
import { collapseToolRuns, groupParts, isPartInteractive } from './messageBubbleModel'
import FormRequest from './FormRequest'
import MarkdownContent from './MarkdownContent'
import MemorySavedCard from './MemorySavedCard'
import MessageRatingControls from './MessageRatingControls'
import OptionSelect from './OptionSelect'
import QuestionSet from './QuestionSet'
import ToolCallGroup from './ToolCallGroup'
import ToolCallRun from './ToolCallRun'
import WriteConfirm from './WriteConfirm'
import WriteConfirmGroup from './WriteConfirmGroup'

export type MessageRating = {
  state: RatingState
  pending: boolean
  onRate: (thumbsUp: boolean) => void
  onSubmitRationale: (text: string) => void
}

type Props = {
  message: ChatMessage
  isStreaming?: boolean
  expandableToolCalls?: boolean
  compactToolCalls?: boolean
  rating?: MessageRating
  onSendMessage?: (text: string) => void
  onDecide?: (toolCallId: string, approved: boolean) => void
  onGroupDecide?: (groupId: string, approved: boolean) => void
  onSubmitForm?: (formId: string, values: Record<string, unknown>) => void
  onSubmitQuestionSet?: (questionSetId: string, answers: Record<string, unknown>) => void
  onLookupSearch?: (query: LookupQuery) => Promise<LookupResult>
  onLookupCreateRequest?: (entity: string, query: string) => void
  optionsInteractive?: boolean
}

const MessageBubble = ({
  message,
  isStreaming,
  expandableToolCalls,
  compactToolCalls,
  rating,
  onSendMessage,
  onDecide,
  onGroupDecide,
  onSubmitForm,
  onSubmitQuestionSet,
  onLookupSearch,
  onLookupCreateRequest,
  optionsInteractive,
}: Props) => {
  const isUser = message.role === 'user'
  const grouped = groupParts(message.parts)
  const interactive = (part: MessagePart) =>
    isPartInteractive(part, message.parts, !!optionsInteractive)

  return (
    <div style={{
      display: 'flex',
      justifyContent: isUser ? 'flex-end' : 'flex-start',
    }}>
      <div
        style={{
          maxWidth: '80%',
          borderRadius: 8,
          padding: '8px 16px',
          ...(isUser
            ? { background: '#f5f5f5', color: '#333' }
            : { color: '#333' }),
        }}
      >
        {collapseToolRuns(grouped, !!compactToolCalls).map((item) => {
          if ('groups' in item) {
            return (
              <ToolCallRun
                key={item.groups[0].callPart.tool_call_id}
                groups={item.groups}
                expandable={expandableToolCalls}
                isHistorical={expandableToolCalls}
                compactToolCalls={compactToolCalls}
              />
            )
          }
          if ('callPart' in item) {
            return (
              <ToolCallGroup
                key={item.callPart.tool_call_id}
                callPart={item.callPart}
                resultPart={item.resultPart}
                expandable={expandableToolCalls}
                isHistorical={expandableToolCalls}
                compactToolCalls={compactToolCalls}
              />
            )
          }
          // Key by position in the uncollapsed list, so a part keeps its key
          // (and its state) when the flag resolves and a run collapses.
          const i = grouped.indexOf(item)
          const part = item
          switch (part.type) {
            case 'text':
              if (isUser) {
                return (
                  <p key={i} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
                    {part.content}
                  </p>
                )
              }
              return <MarkdownContent key={i} content={part.content} />
            case 'option_select':
              return (
                <OptionSelect
                  key={i}
                  part={part}
                  interactive={interactive(part)}
                  onSelect={onSendMessage ?? (() => {})}
                />
              )
            case 'permission_request':
              return (
                <WriteConfirm
                  key={i}
                  part={part}
                  interactive={interactive(part)}
                  onDecide={onDecide ?? (() => {})}
                />
              )
            case 'permission_group_request':
              return (
                <WriteConfirmGroup
                  key={i}
                  part={part}
                  interactive={interactive(part)}
                  onDecide={onGroupDecide ?? (() => {})}
                />
              )
            case 'form_request':
              return (
                <FormRequest
                  key={i}
                  part={part}
                  interactive={interactive(part)}
                  onSubmit={onSubmitForm ?? (() => {})}
                  onLookupSearch={onLookupSearch}
                  onLookupCreateRequest={onLookupCreateRequest}
                />
              )
            case 'question_set':
              return (
                <QuestionSet
                  key={i}
                  part={part}
                  interactive={interactive(part)}
                  onSubmit={onSubmitQuestionSet ?? (() => {})}
                  onLookupSearch={onLookupSearch}
                  onLookupCreateRequest={onLookupCreateRequest}
                />
              )
            case 'memory_saved':
              return <MemorySavedCard key={part.memory_id} part={part} />
            case 'error':
              return (
                <p key={i} style={{ color: '#ff4d4f' }}>
                  {part.message}
                </p>
              )
          }
        })}
        {isStreaming && <span className="ai-streaming-dot" />}
        {rating && (
          <MessageRatingControls
            state={rating.state}
            pending={rating.pending}
            onRate={rating.onRate}
            onSubmitRationale={rating.onSubmitRationale}
          />
        )}
      </div>
    </div>
  )
}

export default MessageBubble
